import { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import {
  Settings,
  LogOut,
  Users,
  MessageCircle,
  Volume2,
  VolumeX,
  Menu,
  X,
  HelpCircle,
  ArrowLeft,
  ChevronUp,
  ChevronDown,
  ShieldAlert
} from 'lucide-react'
import { useAuthStore } from '../hooks/useAuthStore'
import { useSocket } from '../hooks/useSocket'
import { useUIStore } from '../hooks/useUIStore'
import ChatPanel from './ChatPanel'
import GameStartCountdown from './GameStartCountdown'
import soundController from '../sound/SoundController'
import AudioDebugOverlay from '../sound/AudioDebugOverlay'

const Layout = ({ children }) => {
  const { t } = useTranslation('common')
  const { t: tSettings } = useTranslation('settings')
  const { t: tGame } = useTranslation('game')
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout, isAdmin } = useAuthStore()
  const { connected, onlineUsers, showCountdown, countdownData, hideCountdown, currentGame, leaveGame, unreadMessages } = useSocket()
  const gamePanelExpanded = useUIStore((state) => state.gamePanelExpanded)
  const toggleGamePanel = useUIStore((state) => state.toggleGamePanel)
  const gameControlsOpen = useUIStore((state) => state.gameControlsOpen)
  const toggleGameControls = useUIStore((state) => state.toggleGameControls)
  const gameControlsAvailable = useUIStore((state) => state.gameControlsAvailable)
  const setHeaderHeight = useUIStore((state) => state.setHeaderHeight)
  const headerHeight = useUIStore((state) => state.headerHeight)
  const [isChatOpen, setIsChatOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isSoundMuted, setIsSoundMuted] = useState(false)
  const [soundVolume, setSoundVolume] = useState(0.7)
  const headerRef = useRef(null)

  // The players/settings panel toggle and back button only make sense on the
  // active game's own page — the panel itself still renders in GamePage.jsx
  const isGamePage = location.pathname.startsWith('/game/') && !!currentGame

  // Initialize SoundController
  useEffect(() => {
    const initializeSound = async () => {
      try {
        await soundController.initialize()

        // Load saved sound preferences
        const savedVolume = localStorage.getItem('gamelobby_sound_volume')
        const savedMuted = localStorage.getItem('gamelobby_sound_muted')

        if (savedVolume) {
          const volume = parseFloat(savedVolume)
          setSoundVolume(volume)
          soundController.setVolume(volume)
        }

        if (savedMuted) {
          const muted = savedMuted === 'true'
          setIsSoundMuted(muted)
          soundController.setMuted(muted)
        }
      } catch (error) {
        console.warn('Failed to initialize SoundController:', error)
      }
    }

    initializeSound()
  }, [])

  // Keep the shared header-height store value in sync with the header's
  // actual rendered height — it wraps to 1-3 lines depending on breakpoint
  // and content length, so fixed-position UI below it (toasts, bot banner)
  // can't rely on a hardcoded pixel offset.
  useEffect(() => {
    const el = headerRef.current
    if (!el) return

    // Read getBoundingClientRect().bottom rather than the ResizeObserver
    // entry's contentRect — contentRect is content-box only (excludes the
    // header's py-3 padding + border), which undershot the real rendered
    // height and let toasts/the bot banner clip into the header.
    const observer = new ResizeObserver(() => {
      setHeaderHeight(el.getBoundingClientRect().bottom)
    })
    observer.observe(el)

    return () => observer.disconnect()
  }, [setHeaderHeight])

  // Navigate to game page when joining or starting a game. Only from the
  // lobby — redirecting from ANY non-game path made /settings unreachable
  // mid-game (the menu's Settings click bounced straight back to the game).
  useEffect(() => {
    if (currentGame && (location.pathname === '/lobby' || location.pathname === '/')) {
      navigate(`/game/${currentGame.id}`)
    }
  }, [currentGame, navigate, location.pathname])

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleSoundToggle = () => {
    const newMutedState = !isSoundMuted
    setIsSoundMuted(newMutedState)
    soundController.setMuted(newMutedState)
    localStorage.setItem('gamelobby_sound_muted', newMutedState.toString())

    // Play a test sound when unmuting
    if (!newMutedState) {
      soundController.play('ui_click')
    }
  }

  const handleVolumeChange = (newVolume) => {
    setSoundVolume(newVolume)
    soundController.setVolume(newVolume)
    localStorage.setItem('gamelobby_sound_volume', newVolume.toString())
  }

  const navigation = [
    { name: tSettings('title'), href: '/settings', icon: Settings },
    ...(isAdmin ? [{ name: 'Admin', href: '/admin', icon: ShieldAlert }] : [])
  ]

  // Single logout button, placed in one of two spots: on game pages it sits
  // right beside the connection status (the two read as one "session" group);
  // everywhere else it stays at the far right with the user menu.
  const logoutButton = (
    <button
      onClick={() => {
        soundController.play('ui_click')
        handleLogout()
      }}
      className="p-2 text-gray-300 hover:text-red-400 hover:bg-gray-700 rounded-md transition-colors"
      title={t('logout')}
    >
      <LogOut className="w-5 h-5" />
    </button>
  )

  // Chat toggle, rendered in two spots on game pages: below sm it sits on the
  // top row right after the logout button (so it isn't stranded on the
  // stacked icon row); sm+ (and all non-game pages) keep it in the actions
  // cluster. Both copies are gated by opposite breakpoint classes so only
  // one is ever visible.
  const renderChatButton = (extraClassName = '') => (
    <button
      onClick={() => {
        soundController.play('ui_click')
        setIsChatOpen(!isChatOpen)
      }}
      className={`relative p-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-md transition-colors ${extraClassName}`}
      title={t('toggleChat')}
    >
      <MessageCircle className="w-5 h-5" />
      {!isChatOpen && unreadMessages > 0 && (
        <span className="absolute -top-0.5 -right-0.5 min-w-[1.1rem] h-[1.1rem] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
          {unreadMessages > 99 ? '99+' : unreadMessages}
        </span>
      )}
    </button>
  )

  return (
    // Viewport-height column: header on top, pages fill/scroll in <main> below.
    // Inline 100dvh wins where supported (mobile browser chrome shrinks the
    // usable viewport); older browsers drop it and fall back to h-screen.
    <div className="h-screen flex flex-col bg-gray-900" style={{ height: '100dvh' }}>
      {/* Header */}
      <header ref={headerRef} className="bg-gray-800 border-b border-gray-700 px-4 py-3 relative z-50 flex-shrink-0">
        <div className={`flex items-center justify-between gap-y-2 pr-12 ${isGamePage ? 'flex-wrap' : ''}`}>
          {/* Left-hand group — logo, connection, and (on the game's own
              page, sm+) the game name/status all sit in ONE flex-nowrap
              group with a small shared gap. Keeping them one group (instead
              of separate justify-between siblings) is what keeps the gap
              between "Connected" and the game name tight — with more than
              two top-level items, justify-between spreads leftover space
              into EVERY gap, including this one. */}
          <div className="flex items-center gap-4 min-w-0">
            {/* Logo doubles as the "leave game" back button while on a game
                page — the arrow indicates that combined action; off a game
                page it's just the plain home link */}
            <Link
              to="/lobby"
              onClick={() => { if (isGamePage) leaveGame() }}
              className="flex items-center gap-1.5 text-xl font-bold text-white flex-shrink-0"
              title={isGamePage ? tGame('page.leaveGame') : undefined}
            >
              {isGamePage && <ArrowLeft className="w-5 h-5" />}
              {isGamePage ? (
                <>
                  {/* Below md the full brand crowds the status content, and
                      as a back button "Lobby" says enough */}
                  <span className="hidden md:inline">{t('gameLobby')}</span>
                  <span className="md:hidden">{t('lobby')}</span>
                </>
              ) : (
                t('gameLobby')
              )}
            </Link>

            {/* Connection Status (+ logout on game pages, see logoutButton) */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className="text-sm text-gray-400">
                {connected ? t('connected') : t('disconnected')}
              </span>
              {isGamePage && logoutButton}
              {isGamePage && renderChatButton('sm:hidden')}
            </div>

            {/* Active game status (turn/spin/phase), sm+ only — the
                small-screen version below gets its own full-width row
                instead. Two separate portal slots (desktop/mobile) so
                OldTestamentGame.jsx can portal the same content into
                whichever is visible without a duplicate-DOM-id clash.
                The game name only shows md+ — players in the game don't
                need it, and below md it crowded out the status content. */}
            {isGamePage && (
              <div className="hidden sm:flex items-center gap-2 min-w-0">
                <div className="w-px h-6 bg-gray-700 flex-shrink-0" />
                <div className="hidden md:block text-xl font-bold text-white truncate max-w-[220px] lg:max-w-[320px] flex-shrink-0">
                  {currentGame.name}
                </div>
                <div id="game-header-status-desktop" className="flex items-center gap-2 min-w-0" />
              </div>
            )}

          </div>

          {/* User Actions — navigation, sound, and the game panel/controls
              toggles all live in the hamburger menu (all screen sizes);
              only passive info (player counts, user) plus chat stay here */}
          <div className="flex items-center gap-3">
            {/* Active game players count */}
            {isGamePage && (
              <div
                className="hidden sm:flex items-center gap-2 px-3 py-2 bg-gray-700 rounded-md"
                title={tGame('page.players')}
              >
                <Users className="w-4 h-4 text-gray-300" />
                <span className="text-sm text-gray-300">
                  {currentGame.players?.length || 0}/{currentGame.maxPlayers}
                </span>
              </div>
            )}

            {/* Online Users Count */}
            <div className="hidden sm:flex items-center gap-2 px-3 py-2 bg-gray-700 rounded-md">
              <Users className="w-4 h-4 text-green-400" />
              <span className="text-sm text-gray-300">{onlineUsers.length}</span>
            </div>

            {/* Chat Toggle — hidden below sm on game pages, where the copy
                next to the logout button shows instead */}
            {renderChatButton(isGamePage ? 'hidden sm:block' : '')}

            {/* User Menu */}
            <div className="flex items-center gap-3">
              <div className="hidden sm:block text-right">
                <div className="text-sm font-medium text-white">
                  {user?.username}
                </div>
                {user?.isGuest && (
                  <div className="text-xs text-gray-400">{t('guest')}</div>
                )}
              </div>
              
              {!isGamePage && logoutButton}
            </div>
          </div>

          {/* Active game status, small screens only — its own full row
              below everything else instead of the sm+ inline version
              above (no game name here: it's hidden below md). Last in
              DOM so it naturally wraps below when the container's
              flex-wrap (isGamePage only) is active; needs no explicit
              order since it's always alone on its line (w-full). */}
          {isGamePage && (
            <div className="sm:hidden flex items-center gap-2 w-full">
              <div id="game-header-status-mobile" className="flex items-center gap-2 min-w-0" />
            </div>
          )}
        </div>

        {/* Menu Toggle — pinned to the header's own top-right corner (not
            part of the row's flex flow) so it stays put even when the row
            wraps to extra lines on small game-page screens. Visible at all
            sizes: the menu is the single home for navigation, sound, and
            the game panel/controls toggles. */}
        <button
          onClick={() => {
            soundController.play('ui_click')
            setIsMobileMenuOpen(!isMobileMenuOpen)
          }}
          className="absolute top-3 right-4 p-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-md transition-colors"
        >
          {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>

        {/* Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-3 pt-3 border-t border-gray-700"
            >
              <nav className="flex flex-col gap-2">
                {navigation.map((item) => {
                  const Icon = item.icon
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        location.pathname === item.href
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {item.name}
                    </Link>
                  )
                })}

                {/* Sound on/off — stays open so the state change is visible */}
                <button
                  onClick={handleSoundToggle}
                  className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors text-gray-300 hover:bg-gray-700 hover:text-white text-left"
                >
                  {isSoundMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  {isSoundMuted ? t('unmuteAll') : t('muteAll')}
                </button>

                {/* Players & game settings drawer toggle (game pages) */}
                {isGamePage && (
                  <button
                    onClick={() => {
                      soundController.play('ui_click')
                      toggleGamePanel()
                      setIsMobileMenuOpen(false)
                    }}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors text-left ${
                      gamePanelExpanded
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    }`}
                  >
                    {gamePanelExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    {tGame('page.playersAndSettings')}
                  </button>
                )}

                {/* Game-specific "How to Play" panel toggle — only for games
                    that registered one (gameControlsAvailable, set by the
                    plugin on mount) */}
                {isGamePage && gameControlsAvailable && (
                  <button
                    onClick={() => {
                      soundController.play('ui_click')
                      toggleGameControls()
                      setIsMobileMenuOpen(false)
                    }}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors text-left ${
                      gameControlsOpen
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    }`}
                  >
                    <HelpCircle className="w-4 h-4" />
                    {tGame('page.howToPlay')}
                  </button>
                )}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <div className="flex relative flex-1 min-h-0">
        {/* Main Content Area — pages taller than the viewport scroll here */}
        <main className="flex-1 min-h-0 overflow-y-auto">
          {children}
        </main>

        {/* Chat Panel — fixed overlay at every size: as an in-flow sibling
            (the old md:relative) it stole 320px from <main> and forced the
            game's WebGL canvas to resize on every animation frame. Top is
            the live header height, not a hardcoded offset — the header
            wraps to 1-3 lines. */}
        <AnimatePresence>
          {isChatOpen && (
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              className="fixed right-0 bottom-0 w-80 z-40"
              style={{ top: headerHeight }}
            >
              <ChatPanel onClose={() => setIsChatOpen(false)} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Mobile Chat Overlay */}
      {isChatOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30 md:hidden"
          onClick={() => setIsChatOpen(false)}
        />
      )}

      {/* Temporary on-device audio diagnostics (see ios_AUDIO_FIX.md) */}
      {new URLSearchParams(window.location.search).get('audiodebug') === '1' && (
        <AudioDebugOverlay />
      )}

      {/* Game Start Countdown Modal */}
      <GameStartCountdown
        isVisible={showCountdown}
        onCountdownEnd={hideCountdown}
        gameName={countdownData?.gameName || ''}
        playerCount={countdownData?.playerCount || 0}
      />
    </div>
  )
}

export default Layout